// ────────────────────────────────────────────────────────────────────────────
// cadence-step-predicates — the SINGLE source of truth for "does this step
// generate an AI message?" and "does that message get scored with a numeric
// Carlos rubric?".
//
// These two questions used to be answered by copy-pasted local predicates in
// AnglesSection / PolicySection / StepsSection / SummarySection, and they had
// DRIFTED: the angle/message predicate included `linkedin_comment` (correct —
// comments DO get an angle card) while the rubric predicate excluded it
// (also correct — see below). But StepsSection + SummarySection reused the
// MESSAGE predicate to gate the RUBRIC chip/coverage, so a linkedin_comment
// step was flagged "Sin rúbrica" forever — an amber warning that can NEVER be
// resolved (qa-settings-update REJECTS persisting a comment rubric). This file
// unifies both predicates so message-gating and rubric-gating can never drift
// again.
//
// The minimal step shape both helpers accept — anything with a step_type and an
// optional config_json is enough. Callers pass CadenceStep, FlowStep-derived
// objects, or ad-hoc {step_type, config_json} — all satisfy this.
// ────────────────────────────────────────────────────────────────────────────
export interface StepPredicateInput {
  step_type: string
  config_json?: Record<string, unknown> | null
}

// Step types that ALWAYS generate an AI message → they get an angle card and are
// scored by Carlos. linkedin_connect is NOT here: it only generates a message
// when config_json.send_note === true (a plain Day-0 cold invite does not).
const ALWAYS_MESSAGE_STEP_TYPES: ReadonlySet<string> = new Set<string>([
  'linkedin_message',
  'linkedin_comment',
  'send_email',
  'email_reply',
])

// Mirrors the runtime predicate `stepNeedsContent` in process-queue
// (index.ts:1736-1737): a step generates an AI message iff it is one of the
// always-message types OR it is a linkedin_connect whose config_json.send_note
// is explicitly true. A Day-0 plain invite (send_note null/false) generates NO
// message, so it must not surface a prompt / angle / rubric affordance.
// NOTE: `task` is NOT a message step — the Day-9 BC-attachment task sends no AI
// copy — so it correctly gets NO angle card here.
//
// USE THIS to gate: prompt presence, angle cards (linkedin_comment DOES get an
// angle), and the "Sin generación IA" vs message-step branch.
export function stepGeneratesMessage(step: StepPredicateInput): boolean {
  if (ALWAYS_MESSAGE_STEP_TYPES.has(step.step_type)) return true
  return step.step_type === 'linkedin_connect' && step.config_json?.send_note === true
}

// A step NEEDS a numeric Carlos rubric iff Carlos scores it with a rubric row.
// That set is: every message-generating step EXCEPT linkedin_comment, PLUS the
// Day-9 `task` step (the BC-attachment touch — not a message, but Carlos scores
// its cover copy with a rubric). This mirrors the backend allow-list in
// qa-settings-update (VALID_STEP_TYPES = send_email, linkedin_message,
// email_reply, linkedin_connect, task — comment absent) and the carlos_step_rubric
// rows that persist for `task`.
//
// linkedin_comment is EXCLUDED: Carlos validates comments with rule-based
// structural bans (not a numeric rubric), and qa-settings-update REJECTS
// persisting a comment rubric. So a linkedin_comment step DOES get an angle card
// (stepGeneratesMessage=true) but must NEVER be flagged "Sin rúbrica" — that
// amber warning would be permanently irresoluble.
//
// USE THIS to gate: the rubric chip, the per-step effective-rubric block, the
// PolicySection rubric table rows, and the SummarySection rubric coverage check.
export function stepNeedsRubric(step: StepPredicateInput): boolean {
  if (step.step_type === 'linkedin_comment') return false
  if (step.step_type === 'task') return true
  return stepGeneratesMessage(step)
}
